/* FORGE size finder — measurement in, size out.
 *
 * Binds every [data-forge-finder] on the page independently, so the PDP size-guide
 * drawer can carry one per product without any id collision.
 *
 * Two things this file is careful about:
 *
 * 1. UNITS. The chart's numbers are written in whatever `data-forge-finder-unit` says.
 *    Knee/elbow sleeve options are centimetres ("36.5-39 CM"); belt options are inches
 *    ("33-36\" BELLY"). Everything is normalised into the CHART's unit before comparison,
 *    and the readback echoes the unit the shopper actually typed.
 *
 * 2. GUIDANCE. Between-sizes advice is product-specific and getting it wrong is worse
 *    than staying quiet. "Size down for competition" is correct for a compression sleeve
 *    and flatly wrong for a belt (where the rule is gaining vs. cutting weight) or a shoe
 *    (insole and socks). `data-forge-finder-mode` picks the right copy.
 */
(function () {
  'use strict';

  var CM_PER_IN = 2.54;

  /* Guidance copy per mode. `lower` is the snugger neighbouring size, `upper` the roomier
     one — both may be null at the ends of the chart. */
  var GUIDANCE = {
    compression: {
      exact: function (s, lower) {
        return '<b>Training:</b> take ' + s + '. <b>Competition:</b> ' +
          (lower ? 'consider ' + lower + ' for a tighter fit.' : s + ', already our snuggest.');
      },
      between: function (lower, upper) {
        return 'Between sizes: <b>' + lower + '</b> for competition, <b>' + upper + '</b> for daily training.';
      }
    },
    /* KNEE SLEEVES GET THEIR OWN COPY, not `compression` (2026-08-14).
       The shared compression line offers the SMALLER neighbour for competition. On knee
       sleeves that advice runs against the only asymmetry the data actually shows: in
       ops/data/fit_intelligence.json, 6 customers reported a sleeve they could not get on
       or that numbed the leg, against 0 reporting the opposite. Sizing errors themselves
       run roughly evenly both ways (43 small vs 40 large of 170 fit mentions, chart_bias
       "no systematic bias"), so this is deliberately NOT a "runs small, size up" claim —
       that headline overstates the corpus. It is a rule about which mistake is
       recoverable. Elbow sleeves and cuffs keep `compression`, where the tighter option is
       a real preference and the elbow artwork's own tip says so. */
    knee: {
      exact: function (s, lower) {
        return 'Take <b>' + s + '</b>.' +
          (lower ? ' Want it tighter for competition? ' + lower + ' — but it has to go on.' : '');
      },
      between: function (lower, upper) {
        return 'Between sizes: take <b>' + upper + '</b>. A sleeve that is too tight is unwearable; slightly loose still trains.';
      }
    },
    belt: {
      exact: function (s) {
        return 'Take <b>' + s + '</b>. Gaining muscle or weight, size up. Cutting, size down — a belt worn at either extreme of its range will not seat properly.';
      },
      between: function (lower, upper) {
        return 'Between sizes: <b>' + upper + '</b> if you are gaining, <b>' + lower + '</b> if you are cutting.';
      }
    },
    shoe: {
      exact: function (s) {
        return 'Take <b>' + s + '</b>. Removing the insole, going sockless, or narrow feet — size down.';
      },
      between: function (lower) {
        return 'Between sizes: take <b>' + lower + '</b>. The trainer runs slightly large.';
      }
    },
    apparel: {
      exact: function (s) { return 'Take <b>' + s + '</b>.'; },
      between: function (lower, upper) {
        return 'Between sizes: <b>' + lower + '</b> for a closer fit, <b>' + upper + '</b> for room.';
      }
    }
  };

  document.querySelectorAll('[data-forge-finder]').forEach(function (finder) {
    var chartEl = finder.querySelector('[data-forge-finder-chart]');
    if (!chartEl) { return; }

    var chart = [];
    try {
      JSON.parse(chartEl.textContent).split('\n').forEach(function (line) {
        var p = line.split('|').map(function (s) { return s.trim(); });
        if (p.length >= 3 && !isNaN(parseFloat(p[1])) && !isNaN(parseFloat(p[2]))) {
          chart.push([p[0], parseFloat(p[1]), parseFloat(p[2])]);
        }
      });
    } catch (e) { return; }
    if (!chart.length) { return; }

    /* Sort ascending so "one size down" is always the previous row, regardless of the
       order the option values happen to come back in. */
    chart.sort(function (a, b) { return a[1] - b[1]; });

    var chartUnit = finder.getAttribute('data-forge-finder-unit') === 'in' ? 'in' : 'cm';
    var mode = finder.getAttribute('data-forge-finder-mode') || 'compression';
    var copy = GUIDANCE[mode] || GUIDANCE.apparel;

    /* The unit the SHOPPER is typing in — read off whichever toggle button ships marked
       `on`, never assumed. The markup starts on INCHES for every product (Brandon,
       2026-08-14: US customers), including charts written in cm, and everything below
       converts. Reading it from the DOM means the button that looks selected and the unit
       the maths uses can never disagree — hardcoding `chartUnit` here is what made a
       cm-first finder sit under an inch-first toggle. */
    var startBtn = finder.querySelector('[data-forge-unit].on');
    var unit = startBtn ? startBtn.getAttribute('data-forge-unit') : chartUnit;
    var input = finder.querySelector('[data-forge-finder-input]');
    var out = finder.querySelector('[data-forge-finder-out]');
    var sizeEl = finder.querySelector('[data-forge-finder-size]');
    var noteEl = finder.querySelector('[data-forge-finder-note]');
    var goEl = finder.querySelector('[data-forge-finder-go]');
    if (!input || !out || !sizeEl || !noteEl || !goEl) { return; }

    finder.querySelectorAll('[data-forge-unit]').forEach(function (b) {
      b.addEventListener('click', function () {
        unit = b.getAttribute('data-forge-unit');
        finder.querySelectorAll('[data-forge-unit]').forEach(function (x) {
          x.classList.toggle('on', x === b);
        });
      });
    });

    /* Shopper's number -> the chart's unit. */
    function toChartUnit(v) {
      if (unit === chartUnit) { return v; }
      return unit === 'in' ? v * CM_PER_IN : v / CM_PER_IN;
    }

    /* Chart's number -> the shopper's unit, for anything we QUOTE BACK to them. A shopper
       typing inches must not be told "above the 3XL range (51 cm)" — mixing the two units
       in one sentence is how a chart loses trust. Trailing ".0" is trimmed so 20 reads as
       20, not 20.0. */
    function fromChartUnit(v) {
      if (unit === chartUnit) { return v; }
      return unit === 'in' ? v / CM_PER_IN : v * CM_PER_IN;
    }
    function say(v) {
      return String(Math.round(fromChartUnit(v) * 10) / 10);
    }

    function size() {
      var typed = parseFloat(input.value);
      if (isNaN(typed)) { return; }
      var v = toChartUnit(typed);
      var lo = chart[0], hi = chart[chart.length - 1];
      var res = null, i;

      if (v < lo[1]) {
        res = {
          s: lo[0],
          n: 'Below our smallest range. ' + lo[0] + ' is the closest we make — message us before ordering and we will tell you straight whether it will work.'
        };
      } else if (v > hi[2]) {
        res = {
          s: hi[0],
          n: 'Above the ' + hi[0] + ' range (' + say(hi[2]) + ' ' + unit + '). Message us before ordering and we will tell you straight whether it will work.'
        };
      } else {
        for (i = 0; i < chart.length; i++) {
          if (v >= chart[i][1] && v <= chart[i][2]) {
            res = { s: chart[i][0], n: copy.exact(chart[i][0], i > 0 ? chart[i - 1][0] : null) };
            break;
          }
        }
        if (!res) {
          /* Falls between two published ranges. Two very different situations, and
             conflating them gives bad advice:

             a) a hairline gap where the chart rounds — genuinely "between sizes", so the
                normal size-up / size-down guidance applies;
             b) a range we do not stock at all. The barefoot trainer skips US M6.5 to M8,
                a 1.6cm hole. Telling a 25.4cm foot to "take the smaller size, it runs
                large" would sell them a shoe more than a centimetre short. Say plainly
                that we do not make that size.

             GAP_LIMIT is set just above the widest legitimate rounding gap in our charts
             (knee sleeves round at 0.5 cm; belts at 1 inch). */
          var GAP_LIMIT = chartUnit === 'in' ? 1.2 : 1.0;
          for (i = 0; i < chart.length - 1; i++) {
            if (v > chart[i][2] && v < chart[i + 1][1]) {
              if (chart[i + 1][1] - chart[i][2] > GAP_LIMIT) {
                res = {
                  /* Not "—": a lone dash in the display slot reads as a rendering fault.
                     Name the next step instead. */
                  s: 'Ask us',
                  n: 'We do not currently make a size for that measurement. The nearest we stock are <b>' +
                     chart[i][0] + '</b> and <b>' + chart[i + 1][0] + '</b> — message us and we will tell you straight whether either will work.'
                };
              } else {
                res = {
                  s: chart[i][0] + ' / ' + chart[i + 1][0],
                  n: copy.between(chart[i][0], chart[i + 1][0])
                };
              }
              break;
            }
          }
        }
      }
      if (!res) { return; }

      sizeEl.textContent = res.s;
      noteEl.innerHTML = res.n + ' <span class="meas">(' + typed.toFixed(1) + ' ' + unit + ' measured)</span>';
      out.classList.add('show');
    }

    goEl.addEventListener('click', size);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); size(); } });
  });

  /* Deep links. The homepage fit teaser and the department strips point at
     #forge-size-finder, and the PDP's own "Size guide" control at #forge-size-guide —
     both now live INSIDE a collapsed <details>. Without this the browser scrolls to a
     closed box and the shopper sees nothing, which is exactly the dead-anchor failure
     this system was built to end. Open the containing drawer, then scroll. */
  function revealFromHash(hash) {
    if (!hash || hash.length < 2) { return; }
    var target;
    try { target = document.querySelector(hash); } catch (e) { return; }
    if (!target) { return; }
    var drawer = target.closest('details');
    if (drawer && !drawer.open) { drawer.open = true; }
    /* Re-scroll after opening: the anchor jump already happened against the collapsed
       height, so the position the browser landed on is stale. */
    requestAnimationFrame(function () {
      target.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }

  window.addEventListener('hashchange', function () { revealFromHash(window.location.hash); });
  document.addEventListener('DOMContentLoaded', function () { revealFromHash(window.location.hash); });
  if (document.readyState !== 'loading') { revealFromHash(window.location.hash); }
})();
